import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const EVENT_TYPES = [
  { val: 'lesson',   label: 'Lesson',           emoji: '🐴', color: '#1D9E75' },
  { val: 'show',     label: 'Horse show',        emoji: '🏆', color: '#D4537E' },
  { val: 'farrier',  label: 'Farrier',           emoji: '🔨', color: '#534AB7' },
  { val: 'vet',      label: 'Vet appointment',   emoji: '🩺', color: '#185FA5' },
  { val: 'bodywork', label: 'Bodywork',          emoji: '🙌', color: '#0F6E56' },
  { val: 'worming',  label: 'Worming / vaccine', emoji: '💊', color: '#BA7517' },
  { val: 'dental',   label: 'Dental',            emoji: '🦷', color: '#534AB7' },
  { val: 'other',    label: 'Other',             emoji: '📋', color: '#888780' },
];

// Event types that are typically multi-day
const MULTIDAY_TYPES = ['show'];

// Event types that support recurring
const RECURRING_TYPES = ['lesson', 'farrier', 'bodywork', 'worming', 'dental', 'other'];

const AddEventPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [horses, setHorses] = useState([]);
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    title: '',
    event_type: params.get('type') || 'lesson',
    horse_id: params.get('horse') || '',
    start_date: today,
    start_time: '10:00',
    is_multiday: false,
    end_date: today,
    duration: '60',
    all_day: false,
    provider: '',
    notes: '',
    repeat_type: 'none',
    repeat_until: '',
    reminder_minutes: 60,
    share_to_community: false,
  });

  useEffect(() => {
    api.get('/horses').then(r => setHorses(r.data)).catch(() => {});
    // Auto-set multiday for shows
    if (params.get('type') === 'show') {
      setForm(p => ({ ...p, is_multiday: true, all_day: true }));
    }
  }, []);

  const set = useCallback((f) => (e) => {
    const value = e.target.value;
    setForm(p => ({ ...p, [f]: value }));
  }, []);

  const setVal = useCallback((f, v) => setForm(p => ({ ...p, [f]: v })), []);

  const selectedType = EVENT_TYPES.find(t => t.val === form.event_type);
  const isMultiday = form.is_multiday || MULTIDAY_TYPES.includes(form.event_type);
  const canRecur = RECURRING_TYPES.includes(form.event_type);

  const handleTypeChange = (type) => {
    setVal('event_type', type);
    if (MULTIDAY_TYPES.includes(type)) {
      setForm(p => ({ ...p, event_type: type, is_multiday: true, all_day: true }));
    } else {
      setForm(p => ({ ...p, event_type: type, is_multiday: false }));
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Event title required');
    if (isMultiday && form.end_date < form.start_date) return toast.error('End date must be after start date');
    setSaving(true);
    try {
      let start_datetime, end_datetime;

      if (isMultiday || form.all_day) {
        start_datetime = new Date(`${form.start_date}T00:00:00`).toISOString();
        end_datetime = new Date(`${isMultiday ? form.end_date : form.start_date}T23:59:59`).toISOString();
      } else {
        start_datetime = new Date(`${form.start_date}T${form.start_time}`).toISOString();
        end_datetime = new Date(new Date(start_datetime).getTime() + parseInt(form.duration) * 60000).toISOString();
      }

      const payload = {
        title: form.title,
        event_type: form.event_type,
        horse_id: form.horse_id || null,
        start_datetime,
        end_datetime,
        all_day: isMultiday || form.all_day,
        provider: form.provider,
        notes: form.notes,
        repeat_type: canRecur ? form.repeat_type : 'none',
        repeat_until: form.repeat_until || null,
        reminder_minutes: parseInt(form.reminder_minutes),
        share_to_community: form.share_to_community,
      };

      await api.post('/calendar', payload);
      toast.success('Event added!');
      navigate('/calendar');
    } catch { toast.error('Failed to save event'); }
    finally { setSaving(false); }
  };

  // Calculate number of days for multiday events
  const numDays = isMultiday && form.end_date >= form.start_date
    ? Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000) + 1
    : null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 40 }}>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>×</button>
          <h1 style={{ color: 'white', fontSize: 17, flex: 1 }}>Add event</h1>
        </div>
      </div>

      <div style={{ padding: '12px' }}>

        {/* Event type */}
        <div className="card">
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 }}>Event type</div>
          {EVENT_TYPES.map(t => (
            <div key={t.val} onClick={() => handleTypeChange(t.val)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 'var(--radius-md)', marginBottom: 6, border: `1.5px solid ${form.event_type === t.val ? t.color : 'var(--border-color)'}`, background: form.event_type === t.val ? `${t.color}15` : 'white', cursor: 'pointer' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
              <span style={{ fontSize: 16 }}>{t.emoji}</span>
              <span style={{ fontSize: 13, fontWeight: form.event_type === t.val ? 600 : 400, color: form.event_type === t.val ? t.color : 'var(--text-primary)', flex: 1 }}>{t.label}</span>
              {MULTIDAY_TYPES.includes(t.val) && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Multi-day</span>}
              {RECURRING_TYPES.includes(t.val) && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Repeating</span>}
            </div>
          ))}
        </div>

        {/* Details */}
        <div className="card">
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-input" placeholder={`e.g. ${selectedType?.label || 'Event title'}`} value={form.title} onChange={set('title')} autoComplete="off" />
          </div>

          {horses.length > 0 && (
            <div className="form-group">
              <label className="form-label">Horse</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <div className={`chip ${!form.horse_id ? 'selected' : ''}`} onClick={() => setVal('horse_id', '')}>All horses</div>
                {horses.map(h => (
                  <div key={h.id} className={`chip ${form.horse_id === h.id ? 'selected' : ''}`} onClick={() => setVal('horse_id', h.id)}>{h.name}</div>
                ))}
              </div>
            </div>
          )}

          {/* Multi-day toggle for non-show events */}
          {!MULTIDAY_TYPES.includes(form.event_type) && (
            <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.is_multiday} onChange={e => setVal('is_multiday', e.target.checked)} style={{ accentColor: 'var(--cr-green)' }} />
                Multi-day event
              </label>
              {!form.is_multiday && (
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={form.all_day} onChange={e => setVal('all_day', e.target.checked)} style={{ accentColor: 'var(--cr-green)' }} />
                  All day
                </label>
              )}
            </div>
          )}

          {/* Date fields */}
          {isMultiday ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Start date *</label>
                  <input className="form-input" type="date" value={form.start_date} onChange={set('start_date')} />
                </div>
                <div className="form-group">
                  <label className="form-label">End date *</label>
                  <input className="form-input" type="date" value={form.end_date} min={form.start_date} onChange={set('end_date')} />
                </div>
              </div>
              {numDays && (
                <div style={{ background: 'var(--cr-green-light)', borderRadius: 'var(--radius-md)', padding: '8px 12px', marginBottom: 10, fontSize: 12, color: 'var(--cr-green-dark)', fontWeight: 500 }}>
                  📅 {numDays} day{numDays !== 1 ? 's' : ''} — {new Date(form.start_date + 'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to {new Date(form.end_date + 'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
              )}
            </>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input className="form-input" type="date" value={form.start_date} onChange={set('start_date')} />
              </div>
              {!form.all_day && (
                <div className="form-group">
                  <label className="form-label">Time</label>
                  <input className="form-input" type="time" value={form.start_time} onChange={set('start_time')} />
                </div>
              )}
            </div>
          )}

          {/* Duration — single day non-all-day only */}
          {!isMultiday && !form.all_day && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Duration</label>
                <select className="form-input" value={form.duration} onChange={set('duration')}>
                  <option value="30">30 min</option>
                  <option value="60">1 hour</option>
                  <option value="90">1.5 hours</option>
                  <option value="120">2 hours</option>
                  <option value="180">3 hours</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Remind me</label>
                <select className="form-input" value={form.reminder_minutes} onChange={set('reminder_minutes')}>
                  <option value="15">15 min before</option>
                  <option value="30">30 min before</option>
                  <option value="60">1 hour before</option>
                  <option value="1440">1 day before</option>
                </select>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Provider / location</label>
            <input className="form-input" placeholder="e.g. Coach Tracy · Sunrise Stables" value={form.provider} onChange={set('provider')} autoComplete="off" />
          </div>

          {/* Repeat — only for supported types */}
          {canRecur && !isMultiday && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Repeat</label>
                <select className="form-input" value={form.repeat_type} onChange={set('repeat_type')}>
                  <option value="none">Does not repeat</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 weeks</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              {form.repeat_type !== 'none' && (
                <div className="form-group">
                  <label className="form-label">Repeat until</label>
                  <input className="form-input" type="date" value={form.repeat_until} onChange={set('repeat_until')} />
                </div>
              )}
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Notes</label>
            <textarea className="form-input" placeholder="Any details..." value={form.notes} onChange={set('notes')} rows={2} />
          </div>
        </div>

        <div className="card">
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.share_to_community} onChange={e => setVal('share_to_community', e.target.checked)} style={{ accentColor: 'var(--cr-green)' }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Share to barn community</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Barn members will see this on the community calendar</div>
            </div>
          </label>
        </div>

        <button className="btn btn-primary btn-full btn-lg" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : '✓ Save event'}
        </button>
        <button className="btn btn-secondary btn-full" onClick={() => navigate(-1)} style={{ marginTop: 8 }}>Cancel</button>
      </div>
    </div>
  );
};

export default AddEventPage;
